import Button from "components/button";
import Pagination from "components/pagination";
import { Breadcrumbs } from "components/breadcrumbs";
import { Link } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/solid";
import {
  useCreate,
  useTable,
  useTranslation,
  useUpdate,
} from "@refinedev/core";
import { useMemo } from "react";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";
import { CreateEventStep, EventStepStatus, EventType } from "interfaces";
import { HistoryLogButton } from "components/historyLogButton";
import { _openUrlOnNewTab, isHaveActionPermission } from "utills/helpers";
import { get } from "lodash";
import { EventCard, Filter } from "components/shared/event";

const DEFAULT_PAGE_SIZE = 9;

export const AttractionEventList: React.FC = () => {
  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();
  const { translate } = useTranslation();
  const { tableQuery, filters, setFilters } = useTable<any>({
    resource: "content/events/attractions",
    syncWithLocation: true,
    pagination: {
      mode: "off",
      pageSize: DEFAULT_PAGE_SIZE,
    },
    meta: {
      resource: "data",
      offset: true,
    },
  });

  const { mutate: createMutation } = useCreate();

  const _redirectPreviewPage = (eventId: string) => {
    createMutation(
      {
        resource: "content/events/preview",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          event_id: eventId,
        },
      },
      {
        onSuccess: (data) => {
          const previewUrl = get(data, "data.data.preview_url", "/");
          _openUrlOnNewTab(previewUrl);
        },
      }
    );
  };

  const _toggleActive = (id: string, action: string) => {
    if (window.confirm(`Are you sure you want to ${action} this event?`)) {
      updateMutation(
        {
          resource: "content/events",
          id,
          meta: {
            URLSuffix: action,
            ignoreResourceWrapper: true,
          },
          values: {},
        },
        {
          onSuccess: () => {
            toast.success("Update successfully");
            tableQuery.refetch();
          },
        }
      );
    }
  };

  const _handlePageChanged = (page: number) => {
    const offset = (page - 1) * DEFAULT_PAGE_SIZE;

    const updatedFilters = [...filters].flatMap((item) =>
      "field" in item ? item : []
    );
    const pageIndex = updatedFilters.findIndex(
      (filter) => filter.field === "offset"
    );
    if (pageIndex < 0) {
      updatedFilters.push({ field: "offset", operator: "eq", value: offset });
    } else {
      updatedFilters[pageIndex].value = offset;
    }

    setFilters(updatedFilters);
  };

  if (tableQuery.isLoading) {
    return <FullScreenLoading />;
  }

  return (
    <div className="wrapper">
      <Breadcrumbs
        additionalLabel={
          <div className="flex items-center gap-x-2.5">
            <HistoryLogButton
              resource={`content/events/attractions/history`}
              className="w-11 h-11"
            />
            {isHaveActionPermission("attractions", "events", "create") ? (
              <Link to={"/events/attractions/create"} target="_blank">
                <Button className="w-11 h-11 sm:w-auto">
                  <span className="sr-only">Create event</span>
                  <PlusIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">Create Event</span>
                </Button>
              </Link>
            ) : <></>}
          </div>
        }
      />
      <div className="box-container">
        <Filter
          filters={filters}
          setFilters={setFilters}
        />
      </div>
      <div className="box-container">
        <EventCard
          eventType={EventType.ATTRACTION}
          data={tableQuery?.data?.data}
          pageSize={tableQuery?.data?.pageSize}
          currentPage={tableQuery.data?.paging.current_page}
          {...(isHaveActionPermission("attractions", "events", "edit") && {
            primaryButton: (id: string, status: string) => ({
              label:
                status === EventStepStatus.DRAFT
                  ? "Edit"
                  : status === EventStepStatus.PUBLISHED
                  ? "Unpublish"
                  : "Publish",
              ...(status === EventStepStatus.DRAFT
                ? {
                    href: `/events/attractions/${id}/edit?step=${CreateEventStep.BUILD_EVENT_PAGE}`,
                  }
                : {
                    onClick: () =>
                      _toggleActive(
                        id,
                        status === EventStepStatus.PUBLISHED
                          ? "unpublish"
                          : "publish"
                      ),
                  }),
            }),
          })}
          moreActions={(id: string) => [
            ...(isHaveActionPermission("attractions", "events", "edit")
              ? [
                  {
                    label: translate("edit"),
                    href: `/events/attractions/${id}/edit?step=${CreateEventStep.BUILD_EVENT_PAGE}`,
                    target: "_blank",
                  },
                ]
              : []),
            {
              label: translate("preview"),
              onClick: () => _redirectPreviewPage(id),
            },
          ]}
        />
        <div className="mt-6">
          <Pagination
            currentPage={tableQuery?.data?.paging.current_page}
            totalPage={tableQuery?.data?.paging.total_page}
            total={{
              totalItems: tableQuery?.data?.paging.total_item || 0,
              total: tableQuery?.data?.paging.total_event || 0,
            }}
            onChange={_handlePageChanged}
          />
        </div>
      </div>
      {tableQuery.isFetching && tableQuery.isPreviousData ? (
        <FullScreenLoading />
      ) : (
        <></>
      )}
    </div>
  );
};
